import { create } from 'zustand';
import {
  frameworksApi,
  type CreativeFrameworkV2 as CreativeFramework,
  type FrameworkResearchReport,
  type FrameworkUpdatePayload,
  type FrameworkReferenceAd as ReferenceAd,
} from '@/services/api';

type ViewMode = 'grid' | 'list' | 'research_feed';

interface Filters {
  source_category?: string;
  platform?: string;
  funnel_stage?: string;
  search: string;
}

interface FrameworksState {
  // Library
  frameworks: CreativeFramework[];
  loading: boolean;
  error: string | null;
  view: ViewMode;
  filters: Filters;

  // Detail
  selected: CreativeFramework | null;
  selectedLoading: boolean;
  selectedError: string | null;

  // Reports + reference ads for the selected framework
  reports: FrameworkResearchReport[];
  reportsLoading: boolean;
  referenceAds: ReferenceAd[];
  referenceAdsLoading: boolean;

  // Triggers
  triggeringResearch: boolean;
  triggerError: string | null;

  // Actions
  setView: (view: ViewMode) => void;
  setFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
  clearFilters: () => void;
  fetchAll: () => Promise<void>;
  fetchOne: (frameworkId: string) => Promise<void>;
  fetchReports: (frameworkId: string) => Promise<void>;
  fetchReferenceAds: (frameworkId: string, featuredOnly?: boolean) => Promise<void>;
  triggerResearch: (frameworkId: string) => Promise<string | null>;
  approveReport: (
    frameworkId: string,
    reportId: string,
    proposalIds?: string[],
    notes?: string,
  ) => Promise<void>;
  rejectReport: (frameworkId: string, reportId: string) => Promise<void>;
  updateFramework: (frameworkId: string, payload: FrameworkUpdatePayload) => Promise<void>;
  toggleFeatureReferenceAd: (frameworkId: string, adId: string, featured: boolean) => Promise<void>;
}

export const useFrameworksStore = create<FrameworksState>((set, get) => ({
  frameworks: [],
  loading: false,
  error: null,
  view: 'grid',
  filters: { search: '' },
  selected: null,
  selectedLoading: false,
  selectedError: null,
  reports: [],
  reportsLoading: false,
  referenceAds: [],
  referenceAdsLoading: false,
  triggeringResearch: false,
  triggerError: null,

  setView: (view) => set({ view }),

  setFilter: (key, value) =>
    set((state) => ({ filters: { ...state.filters, [key]: value } })),

  clearFilters: () => set({ filters: { search: '' } }),

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const filters = get().filters;
      const params: Parameters<typeof frameworksApi.list>[0] = {};
      if (filters.source_category) params.source_category = filters.source_category;
      if (filters.platform) params.platform = filters.platform;
      if (filters.funnel_stage) params.funnel_stage = filters.funnel_stage;
      const resp = await frameworksApi.list(params);
      set({ frameworks: resp.frameworks, loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
        loading: false,
      });
    }
  },

  fetchOne: async (frameworkId) => {
    set({ selectedLoading: true, selectedError: null });
    try {
      const fw = await frameworksApi.get(frameworkId);
      set({ selected: fw, selectedLoading: false });
    } catch (err) {
      set({
        selectedError: err instanceof Error ? err.message : String(err),
        selectedLoading: false,
      });
    }
  },

  fetchReports: async (frameworkId) => {
    set({ reportsLoading: true });
    try {
      const reports = await frameworksApi.listReports(frameworkId);
      set({ reports, reportsLoading: false });
    } catch (err) {
      console.error('fetchReports failed', err);
      set({ reportsLoading: false, reports: [] });
    }
  },

  fetchReferenceAds: async (frameworkId, featuredOnly = false) => {
    set({ referenceAdsLoading: true });
    try {
      const resp = await frameworksApi.listReferenceAds(frameworkId, featuredOnly);
      set({ referenceAds: resp.ads, referenceAdsLoading: false });
    } catch (err) {
      console.error('fetchReferenceAds failed', err);
      set({ referenceAds: [], referenceAdsLoading: false });
    }
  },

  triggerResearch: async (frameworkId) => {
    set({ triggeringResearch: true, triggerError: null });
    try {
      const resp = await frameworksApi.triggerResearch(frameworkId, 'manual');
      // Refresh reports
      await get().fetchReports(frameworkId);
      set({ triggeringResearch: false });
      return resp.task_or_report || resp.report_id || null;
    } catch (err) {
      set({
        triggerError: err instanceof Error ? err.message : String(err),
        triggeringResearch: false,
      });
      return null;
    }
  },

  approveReport: async (frameworkId, reportId, proposalIds, notes) => {
    await frameworksApi.approveReport(frameworkId, reportId, {
      proposal_ids: proposalIds,
      reviewer_notes: notes,
    });
    await Promise.all([
      get().fetchOne(frameworkId),
      get().fetchReports(frameworkId),
    ]);
  },

  rejectReport: async (frameworkId, reportId) => {
    await frameworksApi.rejectReport(frameworkId, reportId);
    await get().fetchReports(frameworkId);
  },

  updateFramework: async (frameworkId, payload) => {
    const updated = await frameworksApi.update(frameworkId, payload);
    set({ selected: updated });
    // Refresh listing too
    await get().fetchAll();
  },

  toggleFeatureReferenceAd: async (frameworkId, adId, featured) => {
    await frameworksApi.featureReferenceAd(frameworkId, adId, featured);
    await get().fetchReferenceAds(frameworkId);
  },
}));
